import { useLayoutEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { springs } from '../../../utils/animations';

const SOURCE_RADIUS = 20;
const TARGET_RADIUS = 12;

// The ghost keeps the tile's 16:9 shape at the source card's width,
// centred on the card thumbnail, so a uniform scale lands it on the tile.
const sourceBox = (rect, aspect) => {
  const height = rect.width / aspect;
  return { left: rect.left, top: rect.top + (rect.height - height) / 2, width: rect.width, height };
};

/**
 * Card → carousel flight (§5.1): a fixed ghost of the thumbnail travels to
 * the new tile (or the phone peek bar) and hands over to the real tile.
 */
export default function AddFlight({ flight, onLanded }) {
  const [path, setPath] = useState(null);

  useLayoutEffect(() => {
    const target = document.querySelector(`[data-flight-target="${flight.itemKey}"]`)
      ?? document.querySelector('[data-flight-target="peek"]');
    if (!target) {
      onLanded();
      return;
    }
    target.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    const to = target.getBoundingClientRect();
    const from = sourceBox(flight.from, to.width / to.height);
    const scale = to.width / from.width;
    const frozen = import.meta.env.DEV && window.__turboSyncTestFreezeFlight;
    const end = { x: to.left - from.left, y: to.top - from.top, scale };
    setPath({
      from,
      frozen,
      animate: frozen
        ? { x: end.x / 2, y: end.y / 2, scale: (1 + scale) / 2, opacity: 0.95, borderRadius: SOURCE_RADIUS }
        : { ...end, opacity: 0.9, borderRadius: TARGET_RADIUS / scale },
    });
  }, [flight, onLanded]);

  if (!path) return null;

  return (
    <motion.div
      aria-hidden="true"
      data-testid="playlist-add-flight"
      className="fixed z-40 pointer-events-none overflow-hidden shadow-soft-lg bg-gray-200 dark:bg-elevated-dark-3"
      style={{
        left: path.from.left,
        top: path.from.top,
        width: path.from.width,
        height: path.from.height,
        transformOrigin: '0 0',
      }}
      initial={{ x: 0, y: 0, scale: 1, opacity: 1, borderRadius: SOURCE_RADIUS }}
      animate={path.animate}
      transition={springs.gentle}
      onAnimationComplete={() => {
        if (!path.frozen) onLanded();
      }}
    >
      {flight.thumbnailUrl && <img src={flight.thumbnailUrl} alt="" className="w-full h-full object-cover" />}
    </motion.div>
  );
}
